import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PayableRow, ReceivableRow } from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { AsyncSection } from '@/components/AsyncSection';
import { ReportFilterBar } from '@/components/ReportFilterBar';
import { ReportColumnsButton, ReportFiltersButton } from '@/components/ReportToolbar';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { fetchPayables, fetchReceivables, reportKeys } from '@/features/reports/api';
import { useReportFilters } from '@/features/reports/useReportFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { applyClientFilters } from '@/features/reports/applyClientFilters';
import type { ReportFilterRow } from '@/features/reports/reportFilters';
import { usePortal } from '@/features/portal/PortalContext';

const money = (value: number) => value.toFixed(2);

// Each list keeps its OWN layout: they're different tables side by side,
// so one shared column picker would be ambiguous about which it governed.
const RECEIVABLE_TOGGLEABLE = ['phone', 'outstanding'] as const;
const RECEIVABLE_LABELS: Record<string, string> = { phone: 'Phone', outstanding: 'Outstanding' };
const PAYABLE_TOGGLEABLE = ['phone', 'balance'] as const;
const PAYABLE_LABELS: Record<string, string> = { phone: 'Phone', balance: 'Balance' };

const RECEIVABLE_COLUMNS: Column<ReceivableRow>[] = [
  { key: 'name', header: 'Customer', pinned: true, cell: (row) => row.name },
  { key: 'phone', header: 'Phone', visibility: 'hidden sm:table-cell', cell: (row) => row.phone },
  { key: 'outstanding', header: 'Outstanding', align: 'right', cell: (row) => money(row.outstanding) },
];

const PAYABLE_COLUMNS: Column<PayableRow>[] = [
  { key: 'name', header: 'Supplier', pinned: true, cell: (row) => row.name },
  { key: 'phone', header: 'Phone', visibility: 'hidden sm:table-cell', cell: (row) => row.phone },
  { key: 'balance', header: 'Balance', align: 'right', cell: (row) => money(row.balance) },
];

const FILTER_FIELDS = ['Name', 'Phone'] as const;

/** Search is name-only; Filters additionally reach Phone. Both client-side — these are small, per-tenant lists. */
function byNameAndFilters<T extends { name: string; phone: string }>(
  rows: readonly T[],
  search: string,
  filterRows: readonly ReportFilterRow[],
  matchAny: boolean,
): T[] {
  const needle = search.trim().toLowerCase();
  const named = needle ? rows.filter((row) => row.name.toLowerCase().includes(needle)) : [...rows];
  return applyClientFilters(named, {
    search: '',
    filterRows,
    matchAny,
    fieldValue: (row, field) => (field === 'Name' ? row.name : field === 'Phone' ? row.phone : undefined),
    searchValues: () => [],
  });
}

/**
 * Two point-in-time balance lists, each with its own client-side name search.
 *
 * No new endpoint for the search: `retail-pos`'s own `PayableDb` page filters
 * the same way, over the same small per-tenant row count, so there is no
 * round-trip to save by pushing this server-side.
 */
export function ReceivablesPayablesPage() {
  const { profile } = usePortal();
  const [filters, setFilters] = useReportFilters();
  const [receivableSearch, setReceivableSearch] = useState('');
  const [payableSearch, setPayableSearch] = useState('');
  const [receivableFilters, setReceivableFilters] = useState<ReportFilterRow[]>([]);
  const [receivableMatchAny, setReceivableMatchAny] = useState(true);
  const [payableFilters, setPayableFilters] = useState<ReportFilterRow[]>([]);
  const [payableMatchAny, setPayableMatchAny] = useState(true);
  const receivableLayout = useReportLayout('receivables', RECEIVABLE_TOGGLEABLE);
  const payableLayout = useReportLayout('payables', PAYABLE_TOGGLEABLE);
  const receivableColumns = applyReportLayout(RECEIVABLE_COLUMNS, receivableLayout);
  const payableColumns = applyReportLayout(PAYABLE_COLUMNS, payableLayout);

  const receivablesQuery = useQuery({
    queryKey: reportKeys.receivables(filters.branchId),
    queryFn: ({ signal }) => fetchReceivables(filters.branchId, signal),
  });
  const payablesQuery = useQuery({
    queryKey: reportKeys.payables(filters.branchId),
    queryFn: ({ signal }) => fetchPayables(filters.branchId, signal),
  });

  const filteredReceivables = useMemo(
    () => byNameAndFilters(receivablesQuery.data ?? [], receivableSearch, receivableFilters, receivableMatchAny),
    [receivablesQuery.data, receivableSearch, receivableFilters, receivableMatchAny],
  );
  const filteredPayables = useMemo(
    () => byNameAndFilters(payablesQuery.data ?? [], payableSearch, payableFilters, payableMatchAny),
    [payablesQuery.data, payableSearch, payableFilters, payableMatchAny],
  );

  return (
    <div>
      <PageHeader
        title="Receivables & payables"
        description="Outstanding customer balances and supplier balances, as of now — not filtered by date."
      />
      <ReportFilterBar filters={filters} onChange={setFilters} branches={profile?.branches ?? []} hideDateRange />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <section>
          <div className="mb-3 flex items-center justify-between gap-4">
            <h2 className="text-sm font-semibold text-slate-900">Receivables</h2>
            <div className="flex items-center gap-2">
              <input
                type="search"
                className="input w-40"
                placeholder="Search customer"
                aria-label="Search receivables by customer name"
                value={receivableSearch}
                onChange={(event) => setReceivableSearch(event.target.value)}
              />
              <ReportFiltersButton
                fields={FILTER_FIELDS}
                applied={receivableFilters}
                matchAny={receivableMatchAny}
                onApply={(rows, matchAny) => {
                  setReceivableFilters(rows);
                  setReceivableMatchAny(matchAny);
                }}
                placeholderFor={(field) => (field === 'Phone' ? 'phone' : 'name')}
              />
              <ReportColumnsButton layout={receivableLayout} labels={RECEIVABLE_LABELS} />
            </div>
          </div>
          <AsyncSection
            state={receivablesQuery}
            isEmpty={() => filteredReceivables.length === 0}
            emptyTitle={receivableSearch ? 'No customers match that search' : 'No outstanding receivables'}
            skeleton={<TableSkeleton columns={3} />}
          >
            {() => (
              <DataTable
                caption="Receivables"
                layout={receivableLayout}
                columns={receivableColumns}
                rows={filteredReceivables}
                rowKey={(row) => String(row.customerSourceId)}
                totals={{ label: 'Total', cells: { outstanding: money(filteredReceivables.reduce((acc, row) => acc + row.outstanding, 0)) } }}
              />
            )}
          </AsyncSection>
        </section>

        <section>
          <div className="mb-3 flex items-center justify-between gap-4">
            <h2 className="text-sm font-semibold text-slate-900">Payables</h2>
            <div className="flex items-center gap-2">
              <input
                type="search"
                className="input w-40"
                placeholder="Search supplier"
                aria-label="Search payables by supplier name"
                value={payableSearch}
                onChange={(event) => setPayableSearch(event.target.value)}
              />
              <ReportFiltersButton
                fields={FILTER_FIELDS}
                applied={payableFilters}
                matchAny={payableMatchAny}
                onApply={(rows, matchAny) => {
                  setPayableFilters(rows);
                  setPayableMatchAny(matchAny);
                }}
                placeholderFor={(field) => (field === 'Phone' ? 'phone' : 'name')}
              />
              <ReportColumnsButton layout={payableLayout} labels={PAYABLE_LABELS} />
            </div>
          </div>
          <AsyncSection
            state={payablesQuery}
            isEmpty={() => filteredPayables.length === 0}
            emptyTitle={payableSearch ? 'No suppliers match that search' : 'No outstanding payables'}
            skeleton={<TableSkeleton columns={3} />}
          >
            {() => (
              <DataTable
                caption="Payables"
                layout={payableLayout}
                columns={payableColumns}
                rows={filteredPayables}
                rowKey={(row) => String(row.supplierSourceId)}
                totals={{ label: 'Total', cells: { balance: money(filteredPayables.reduce((acc, row) => acc + row.balance, 0)) } }}
              />
            )}
          </AsyncSection>
        </section>
      </div>
    </div>
  );
}
